import React, { useState, useEffect } from "react";
import { MapContainer, TileLayer, GeoJSON, Marker, Popup } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

import { STARLINK_SITES } from "../data/starlinkLocations";

export default function MapaQuintanaRoo() {
  const [geoData, setGeoData] = useState(null);
  const [selectedMunicipio, setSelectedMunicipio] = useState(null);
  const [selectedInfo, setSelectedInfo] = useState(null);

  // ===========================
  // CARGAR GEOJSON DESDE PUBLIC
  // ===========================
  useEffect(() => {
    fetch("/data/quintanaRooMunicipios.geojson")
      .then((res) => res.json())
      .then((json) => setGeoData(json))
      .catch((err) => console.error("Error cargando GeoJSON:", err));
  }, []);

  // ===========================
  // CALCULAR MÉTRICAS
  // ===========================
  function getInfoByMunicipio(nombreMunicipio) {
    const muni = nombreMunicipio.trim().toUpperCase();
    const inv = window.__INVENTARIO_GLOBAL__ || [];

    // 📡 Antenas Starlink
    const starlinkCount = STARLINK_SITES.filter(
      (s) => s.municipio.trim().toUpperCase() === muni
    ).length;

    // 💼 Equipos asignados
    const equiposMunicipio = inv.filter(
      (i) => (i.municipio || "").trim().toUpperCase() === muni
    );
    const equiposCount = equiposMunicipio.length;

    // 🧱 Módulos activos únicos
    const moduloSet = new Set(
      equiposMunicipio.map((i) => i.modulo).filter(Boolean)
    );
    const modulosActivos = moduloSet.size;

    // ❤️ Salud operativa
    const buenos = equiposMunicipio.filter(
      (i) => (i.condicion || "").toUpperCase() === "BUENO"
    ).length;
    const salud =
      equiposCount === 0 ? 0 : Math.round((buenos / equiposCount) * 100);

    // 🟥🟨🟩 Estatus
    let estatus = "OK";
    if (modulosActivos === 0 || salud < 40) estatus = "CRÍTICO";
    else if (modulosActivos < 3 || salud < 75) estatus = "INCOMPLETO";

    return { starlinkCount, equiposCount, modulosActivos, salud, estatus };
  }

  // ===========================
  // COLOR POR ESTATUS
  // ===========================
  function colorPorEstatus(estatus) {
    switch (estatus) {
      case "OK":
        return "#22c55e";
      case "INCOMPLETO":
        return "#f59e0b";
      case "CRÍTICO":
      default:
        return "#ef4444";
    }
  }

  // ===========================
  // ICONO STARLINK ANIMADO
  // ===========================
  const starlinkIcon = L.divIcon({
  className: "starlink-wrapper",
  html: [
    '<div class="starlink-pulse"></div>',
    '<div class="starlink-radar"></div>',
    '<img src="/icons/starlink.png" class="starlink-img" />'
  ].join(""),
  iconSize: [42, 42],
  iconAnchor: [21, 21],
  popupAnchor: [0, -24]
});


  // ===========================
  // MANEJO DE MUNICIPIO
  // ===========================
  function onEachMunicipio(feature, layer) {
    const nombre = feature.properties?.NOMGEO || "SIN NOMBRE";
    const info = getInfoByMunicipio(nombre);
    const color = colorPorEstatus(info.estatus);

    layer.setStyle({
      fillColor: color,
      fillOpacity: 0.45,
      weight: 1.2,
      color: "#1e3a8a",
    });

    // Tooltip avanzado
    layer.bindTooltip(
      `
      <div style="font-size:12px;padding:4px;">
        <strong>${nombre}</strong><br/>
        ✔ Antenas Starlink: <strong>${info.starlinkCount}</strong><br/>
        ✔ Módulos activos: <strong>${info.modulosActivos}</strong><br/>
        ✔ Equipos asignados: <strong>${info.equiposCount}</strong><br/>
        ✔ Salud operativa: <strong>${info.salud}%</strong><br/><br/>
        <span style="color:${color};font-weight:600;">
          ● ${info.estatus}
        </span>
      </div>
    `,
      { sticky: true }
    );

    layer.on("click", () => {
      setSelectedMunicipio(nombre);
      setSelectedInfo(info);
    });
  }

  // ===========================
  // RENDER
  // ===========================
  return (
    <div className="relative">
      {/* MAPA */}
      <MapContainer
        center={[19.5, -88.5]}
        zoom={7}
        style={{ height: "450px", width: "100%" }}
      >
        <TileLayer
          attribution="OpenStreetMap"
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />

        {/* Municipios */}
        {geoData && <GeoJSON data={geoData} onEachFeature={onEachMunicipio} />}

        {/* Antenas Starlink */}
        {STARLINK_SITES.map((site) => (
          <Marker
            key={site.id}
            position={[site.lat, site.lng]}
            icon={starlinkIcon}
          >
            <Popup>
              <strong>{site.nombre}</strong>
              <br />
              {site.municipio}
              <br />
              <span style={{ fontSize: "12px" }}>{site.tipo}</span>
            </Popup>
          </Marker>
        ))}
      </MapContainer>

      {/* LEYENDA */}
      <div className="absolute left-3 bottom-3 bg-white/90 rounded-lg px-3 py-2 text-[11px] shadow border border-slate-200 space-y-1 z-[9000]">
        <div className="font-semibold text-slate-700 mb-1">
          Estatus de módulos
        </div>

        <div className="flex items-center gap-2">
          <span className="inline-block w-3 h-3 rounded-full bg-emerald-500" />
          <span>OK — Completos</span>
        </div>

        <div className="flex items-center gap-2">
          <span className="inline-block w-3 h-3 rounded-full bg-amber-500" />
          <span>Incompleto</span>
        </div>

        <div className="flex items-center gap-2">
          <span className="inline-block w-3 h-3 rounded-full bg-red-500" />
          <span>Crítico</span>
        </div>
      </div>

      {/* PANEL LATERAL */}
      {selectedMunicipio && selectedInfo && (
        <div className="absolute top-0 right-0 h-full w-80 bg-white shadow-2xl border-l border-slate-300 p-4 overflow-y-auto z-[9999]">
          <h2 className="text-lg font-bold text-slate-900 mb-2">
            {selectedMunicipio}
          </h2>

          <div className="space-y-2 text-sm">
            <p>
              📡 Antenas Starlink: <strong>{selectedInfo.starlinkCount}</strong>
            </p>
            <p>
              🧱 Módulos activos:{" "}
              <strong>{selectedInfo.modulosActivos}</strong>
            </p>
            <p>
              💼 Equipos asignados:{" "}
              <strong>{selectedInfo.equiposCount}</strong>
            </p>
            <p>
              ❤️ Salud operativa:{" "}
              <strong className="text-emerald-600">
                {selectedInfo.salud}%
              </strong>
            </p>

            <p>
              🟢 Estatus:{" "}
              <strong
                className={
                  selectedInfo.estatus === "OK"
                    ? "text-emerald-600"
                    : selectedInfo.estatus === "INCOMPLETO"
                    ? "text-amber-600"
                    : "text-red-600"
                }
              >
                {selectedInfo.estatus}
              </strong>
            </p>
          </div>

          <button
            onClick={() => setSelectedMunicipio(null)}
            className="mt-4 bg-slate-900 text-white px-4 py-2 rounded-lg shadow hover:bg-slate-700 w-full"
          >
            Cerrar
          </button>
        </div>
      )}
    </div>
  );
}
